import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { Box, Button, CircularProgress, Stack, Typography } from '@metarouter-private/mr-mui';

import { MrAppLayout } from '@ion/components';
import SideNav from 'app/components/side-nav';
import RedirectFeatureGate from 'app/launch-darkly/gates/redirect-feature-gate';
import { FEATURE_FLAGS } from 'app/launch-darkly/featureFlags';

// First-class pages import this barrel, so MetaTagger loads only when its page renders, and an error
// in it shows here, reported to Sentry, while the rest of Ion keeps working.
const MetaTaggerAppPage = lazy(() => import('./metatagger'));

// Ion's layout around the loading and error states, so the side nav stays usable.
function Shell({ children }) {
  return (
    <MrAppLayout title="MetaTagger" description="Tag management" sideNav={<SideNav />}>
      <Box p={4}>{children}</Box>
    </MrAppLayout>
  );
}

Shell.propTypes = {
  children: PropTypes.node,
};

// A failed load stays failed until the page reloads.
function LoadFailed() {
  return (
    <Shell>
      <Stack spacing={2} alignItems="flex-start">
        <Typography>MetaTagger couldn&apos;t load.</Typography>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Stack>
    </Shell>
  );
}

export default function LazyMetaTaggerAppPage(props) {
  return (
    <RedirectFeatureGate flagName={FEATURE_FLAGS.metatagger}>
      <Sentry.ErrorBoundary fallback={<LoadFailed />}>
        <Suspense
          fallback={
            <Shell>
              <CircularProgress />
            </Shell>
          }
        >
          <MetaTaggerAppPage {...props} />
        </Suspense>
      </Sentry.ErrorBoundary>
    </RedirectFeatureGate>
  );
}
